import { LOADING_TEXTS } from '@/hooks/generator-constants';
import { playCompletionAlerts } from '@/lib/notification-utils';
import { getPusherClient } from '@/lib/pusher-client';
import { playToastSound } from '@/lib/sound-utils';
import { getUserContentAction } from '@/server/actions/sora/content-actions';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

// Time after which client-side DB polling begins (while simulation animation continues)
import {
  COUNTDOWN_LOOP_PAUSE_MS,
  POLLING_START_DELAY,
  SIMULATION_DURATION,
} from '@/hooks/generator-constants';

export interface ActiveGeneration {
  taskId: string;
  prompt: string;
  imageUrl: string | null;
  // phase: controls the UI progress bar animation
  //   'simulating' = progress bar counting up (0→100 over SIMULATION_DURATION)
  //   'polling'    = simulation done, showing completed/result state
  phase: 'simulating' | 'polling';
  // isPolling: separate flag — true when client SHOULD be querying the DB.
  //   Becomes true at POLLING_START_DELAY (60s), before phase switches to 'polling'.
  //   Safe across page refreshes: recovered from `createdAt` timestamp.
  isPolling: boolean;
  progress: number;
  startTime: number; // Date.now() when simulation started
  pollingStartTime?: number; // Date.now() when phase switched to polling
  deferredError: string | null;
  knownFailed?: boolean; // True if API returned failed status immediately
  status?: string; // Store the initial status (e.g. 'failed', 'rejected')
  failureReason?: string | null; // Store the initial failure reason
  aspectRatio?: string;
  mediaType?: 'video' | 'audio' | 'image';
  isRefunded?: boolean;
  countdownDuration: number;
  // loopCount: number of times the countdown has restarted (0 = not yet looped)
  loopCount: number;
  // isPausing: true during the 1-second pause at 100% before restart
  isPausing: boolean;
  // pauseStartTime: set when entering pause, used to measure 1s elapsed
  pauseStartTime?: number;
}

interface UseGenerationQueueProps {
  isAuthenticated: boolean;
  isAuthInitialized: boolean;
  onCreditUpdate: () => void;
  initialContent?: any[] | null;
  userId?: string;
  onAutoSwitch?: (
    serviceSlug: string,
    params: { prompt: string; aspectRatio: string }
  ) => Promise<boolean>;
}

export function useGenerationQueue({
  isAuthenticated,
  isAuthInitialized,
  onCreditUpdate,
  initialContent = null,
  userId,
  onAutoSwitch,
}: UseGenerationQueueProps) {
  const [userContent, setUserContent] = useState<any[]>(initialContent || []);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!initialContent);
  const [activeGenerations, setActiveGenerations] = useState<Map<string, ActiveGeneration>>(
    new Map()
  );
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [loadingTextAutoplay, setLoadingTextAutoplay] = useState(true);

  const [galleryState, setGalleryState] = useState<{
    row1: number[];
    row2: number[];
    row3: number[];
    row4: number[];
    row5: number[];
  } | null>(null);

  // Gallery Data
  useEffect(() => {
    const createGalleryData = () => {
      const totalPortraitContent = 16;
      const totalLandscapeContent = 9;
      const totalSquareContent = 5;

      const getRandomNumbers = (total: number, count: number) => {
        const numbers = Array.from({ length: total }, (_, i) => i + 1);
        for (let i = numbers.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          const temp = numbers[i];
          numbers[i] = numbers[j]!;
          numbers[j] = temp!;
        }
        return numbers.slice(0, count);
      };

      const selectedPortraitContent = getRandomNumbers(totalPortraitContent, 9);
      const selectedLandscapeContent = getRandomNumbers(totalLandscapeContent, 3);
      const selectedSquareContent = getRandomNumbers(totalSquareContent, 3);

      setGalleryState({
        row1: selectedPortraitContent.slice(0, 3),
        row2: selectedLandscapeContent.slice(0, 3),
        row3: selectedPortraitContent.slice(3, 6),
        row4: selectedSquareContent.slice(0, 3),
        row5: selectedPortraitContent.slice(6, 9),
      });
    };
    createGalleryData();
  }, []);

  const initialFetchDone = useRef(!!initialContent);

  // Reset state on logout
  useEffect(() => {
    if (isAuthInitialized && !isAuthenticated) {
      setUserContent([]);
      setActiveGenerations(new Map());
      setIsGenerating(false);
      setIsInitialLoading(false);
      initialFetchDone.current = false; // Reset so gallery re-fetches on next login
    }
  }, [isAuthenticated, isAuthInitialized]);

  // Fetch Content & Recover Generation State
  useEffect(() => {
    const fetchContent = async () => {
      // If we haven't checked auth yet, don't stop loading
      if (!isAuthInitialized) return;

      if (isAuthenticated && !initialFetchDone.current) {
        try {
          const result = await getUserContentAction(undefined, true);
          if (result.success && result.data) {
            const sortedData = [...result.data].sort((a, b) => {
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              return dateB - dateA;
            });
            setUserContent(sortedData);

            // If we found content that were just refunded (or failed and unrefunded previously),
            // trigger a credit refresh to sync the Header balance.
            const hasRecentlyRefunded = result.data.some(
              (c: any) =>
                c.status === 'failed' &&
                (c.isRefunded || c.failureReason?.toLowerCase().includes('refund'))
            );
            if (hasRecentlyRefunded) {
              onCreditUpdate();
            }

            if (activeGenerations.size === 0 && !isGenerating) {
              const pendingContent = result.data.filter((c: any) => {
                const status = c.status;
                if (status === 'pending' || status === 'processing') return true;

                if (status === 'completed') {
                  const age = Date.now() - new Date(c.createdAt).getTime();
                  return age < 60 * 1000; // Keep recently completed for toast
                }
                return false;
              });

              if (pendingContent.length > 0) {
                const newGenerations = new Map<string, ActiveGeneration>();
                for (const pc of pendingContent) {
                  const tid = pc.taskId || pc.id;
                  // If we already added this to activeGenerations elsewhere, skip
                  if (activeGenerations.has(tid)) continue;

                  if (pc.status === 'failed') continue;

                  const elapsed = Date.now() - new Date(pc.createdAt).getTime();
                  const totalDuration = (pc.countdownDuration || 150) * 1000;
                  const cycleMs = totalDuration + COUNTDOWN_LOOP_PAUSE_MS;
                  const loopCount = Math.floor(elapsed / cycleMs);
                  const cycleElapsed = Math.max(0, elapsed % cycleMs);
                  const isPausing = cycleElapsed >= totalDuration;
                  const p = isPausing ? 100 : Math.min((cycleElapsed / totalDuration) * 100, 100);

                  // Recover isPolling: true if enough time has elapsed for polling to have started
                  const pollingDelay = Math.min(60000, totalDuration * 0.4);
                  const recoveredIsPolling = elapsed >= pollingDelay;

                  let recoveredAspectRatio = '16:9';
                  try {
                    const settings =
                      typeof pc.settings === 'string' ? JSON.parse(pc.settings) : pc.settings;
                    if (settings?.aspectRatio) {
                      recoveredAspectRatio = settings.aspectRatio;
                    }
                  } catch (e) {
                    console.error('Failed to parse settings for aspect ratio recovery', e);
                  }

                  newGenerations.set(tid, {
                    taskId: tid,
                    prompt: pc.prompt,
                    imageUrl: null,
                    status: pc.status,
                    failureReason: pc.failureReason || null,
                    phase: 'simulating',
                    isPolling: recoveredIsPolling,
                    progress: p,
                    startTime: Date.now() - elapsed,
                    pollingStartTime: recoveredIsPolling
                      ? Date.now() - (elapsed - pollingDelay)
                      : undefined,
                    deferredError: null,
                    aspectRatio: recoveredAspectRatio,
                    mediaType: pc.mediaType as 'video' | 'audio' | 'image' | undefined,
                    isRefunded: (pc as any).isRefunded,
                    countdownDuration: pc.countdownDuration || 150,
                    loopCount: loopCount,
                    isPausing: isPausing,
                    pauseStartTime: isPausing
                      ? Date.now() - (cycleElapsed - totalDuration)
                      : undefined,
                  });
                }
                if (newGenerations.size > 0) {
                  setActiveGenerations(newGenerations);
                  setIsGenerating(true);
                  setLoadingTextIndex(0);
                }
              }
            }
          }
        } catch (err) {
          console.error('Fetch content error:', err);
        } finally {
          setIsInitialLoading(false);
        }
        initialFetchDone.current = true;
      } else if (isAuthInitialized) {
        // Only mark done if auth is initialized (user is genuinely not logged in)
        // Don't mark done if auth hasn't initialized yet — we're still waiting
        setIsInitialLoading(false);
      }
    };
    fetchContent();
  }, [isAuthenticated, activeGenerations.size, isGenerating, isAuthInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve userId: prefer prop (from auth store), fallback to first content item
  const resolvedUserId = userId || (userContent.length > 0 ? userContent[0].userId : null);

  // Real-time updates subscription — re-runs when resolvedUserId changes
  useEffect(() => {
    if (!isAuthenticated || !isAuthInitialized) return;

    // Wait until we have a userId
    if (!resolvedUserId) return;

    const pusher = getPusherClient();
    if (!pusher) return;

    const channelName = `user-${resolvedUserId}`;
    console.log(`[Realtime] Subscribing to channel: ${channelName}`);
    const channel = pusher.subscribe(channelName);

    const syncData = async () => {
      const result = await getUserContentAction(undefined, true);
      if (result.success && result.data) {
        const sortedData = [...result.data].sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });
        setUserContent(sortedData);

        // Update active generations based on the new data
        setActiveGenerations((prev) => {
          const updated = new Map(prev);
          let changed = false;
          let shouldRefreshCredits = false;

          for (const [key, gen] of updated) {
            const content = result.data.find(
              (c: any) => c.id === gen.taskId || c.taskId === gen.taskId
            );

            if (content) {
              if (content.status === 'completed' || content.status === 'failed') {
                if (content.status === 'completed') {
                  const typeLabel =
                    gen.mediaType === 'audio'
                      ? 'SFX'
                      : gen.mediaType === 'image'
                        ? 'Image'
                        : 'Video';
                  playToastSound('success');
                  toast.success(`${typeLabel} generation complete!`, {
                    id: `complete-${gen.taskId}`,
                  });
                  playCompletionAlerts(`${typeLabel} complete!`, 'Your generation is finished.');

                  shouldRefreshCredits = true;
                  updated.delete(key);
                  changed = true;
                } else {
                  if (!gen.knownFailed) {
                    updated.delete(key);
                    changed = true;
                  }

                  const isAutoSwitched = content.failureReason === 'Auto-Switch Activated';
                  const isRefunded =
                    (content as any).isRefunded ||
                    content.failureReason?.toLowerCase().includes('refund');

                  if (isAutoSwitched) {
                    playToastSound('success');
                    toast.success('Auto-Switch Activated', {
                      icon: '🔄',
                    });
                  } else if (isRefunded) {
                    playToastSound('info');
                    toast.success(content.failureReason || 'Generation failed. Credits refunded.', {
                      icon: '✅',
                    });
                  } else {
                    if (!gen.knownFailed) playToastSound('error');
                    toast.error(content.failureReason || 'Generation failed.');
                  }

                  shouldRefreshCredits = true;
                }
              }
            }
          }

          const pendingContent = result.data.filter(
            (c: any) => c.status === 'pending' || c.status === 'processing'
          );
          for (const pc of pendingContent) {
            const tid = pc.taskId || pc.id;
            if (!updated.has(tid)) {
              let recoveredAspectRatio = '16:9';
              try {
                const settings =
                  typeof pc.settings === 'string' ? JSON.parse(pc.settings) : pc.settings;
                if (settings?.aspectRatio) {
                  recoveredAspectRatio = settings.aspectRatio;
                }
              } catch (e) {}

              const elapsed = Date.now() - new Date(pc.createdAt).getTime();
              const totalDuration = (pc.countdownDuration || 150) * 1000;
              const cycleMs = totalDuration + COUNTDOWN_LOOP_PAUSE_MS;
              const loopCount = Math.floor(elapsed / cycleMs);
              const cycleElapsed = Math.max(0, elapsed % cycleMs);
              const isPausing = cycleElapsed >= totalDuration;
              const p = isPausing ? 100 : Math.min((cycleElapsed / totalDuration) * 100, 100);

              const pollingDelay = Math.min(60000, totalDuration * 0.4);
              const recoveredIsPolling = elapsed >= pollingDelay;

              updated.set(tid, {
                taskId: tid,
                prompt: pc.prompt,
                imageUrl: null,
                status: pc.status,
                failureReason: pc.failureReason || null,
                phase: 'simulating',
                isPolling: recoveredIsPolling,
                progress: p,
                startTime: Date.now() - elapsed,
                pollingStartTime: recoveredIsPolling
                  ? Date.now() - (elapsed - pollingDelay)
                  : undefined,
                deferredError: null,
                aspectRatio: recoveredAspectRatio,
                mediaType: pc.mediaType as 'video' | 'audio' | 'image' | undefined,
                isRefunded: (pc as any).isRefunded,
                countdownDuration: pc.countdownDuration || 150,
                loopCount: loopCount,
                isPausing: isPausing,
                pauseStartTime: isPausing ? Date.now() - (cycleElapsed - totalDuration) : undefined,
              });
              changed = true;

              setTimeout(() => setIsGenerating(true), 0);
            }
          }

          if (updated.size === 0) {
            setTimeout(() => setIsGenerating(false), 0);
          }

          if (shouldRefreshCredits) {
            setTimeout(() => onCreditUpdate(), 0);
          }

          return changed ? updated : prev;
        });
      }
    };

    const handleContentUpdate = async () => {
      console.log('[Realtime] Content update received, refreshing gallery...');
      await syncData();
    };

    channel.bind('video-updated', handleContentUpdate);
    channel.bind('content-updated', handleContentUpdate);

    // Mobile fallback: re-fetch when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Realtime] Tab became visible, re-fetching gallery...');
        syncData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log(`[Realtime] Unsubscribing from channel: ${channelName}`);
      pusher.unsubscribe(channelName);
      channel.unbind_all();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isAuthInitialized, resolvedUserId, onCreditUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading Text Rotation
  useEffect(() => {
    if (activeGenerations.size > 0 && loadingTextAutoplay) {
      const interval = setInterval(() => {
        setLoadingTextIndex((prev) => (prev + 1) % LOADING_TEXTS.length);
      }, 5000);
      return () => clearInterval(interval);
    }
    if (activeGenerations.size === 0) {
      setLoadingTextIndex(0);
      setLoadingTextAutoplay(true);
    }
  }, [activeGenerations.size, loadingTextAutoplay]);

  // Encouragement Nudge
  useEffect(() => {
    if (activeGenerations.size > 0) {
      const timer = setTimeout(() => setShowEncouragement(true), 2000);
      return () => clearTimeout(timer);
    }
    setShowEncouragement(false);
  }, [activeGenerations.size]);

  // Simulation Effect — drives the progress bar animation (0→100 over countdownDuration)
  // Also responsible for:
  //   - Enabling isPolling flag at 40% of duration (or min 60s) for early DB polling
  //   - Looping the countdown indefinitely: when it hits zero, pause 1s then restart
  useEffect(() => {
    const simulatingGens = Array.from(activeGenerations.values()).filter(
      (g) => g.phase === 'simulating'
    );
    if (simulatingGens.length === 0) return;

    const interval = setInterval(async () => {
      const now = Date.now();
      let needsCreditRefresh = false;
      let needsContentFetch = false;
      let stateChanged = false;

      const updated = new Map(activeGenerations);

      for (const [key, gen] of activeGenerations) {
        if (gen.phase !== 'simulating') continue;

        const totalDuration = (gen.countdownDuration || 150) * 1000;

        // Derive effective elapsed time, accounting for completed loop cycles.
        // Each completed cycle = totalDuration + COUNTDOWN_LOOP_PAUSE_MS.
        const cycleMs = totalDuration + COUNTDOWN_LOOP_PAUSE_MS;
        const effectiveElapsed = now - (gen.startTime + gen.loopCount * cycleMs);

        if (gen.isPausing) {
          // We are in the 1-second pause at 100%. Check if pause has elapsed.
          if (now - (gen.pauseStartTime ?? now) >= COUNTDOWN_LOOP_PAUSE_MS) {
            // Pause is over — restart the countdown.
            updated.set(key, {
              ...gen,
              isPausing: false,
              loopCount: gen.loopCount + 1,
              progress: 0,
            });
            stateChanged = true;
          }
          // Otherwise still pausing — keep progress at 100%, no changes needed.
        } else if (effectiveElapsed >= totalDuration) {
          // Countdown just hit zero. Enter the pause phase.
          if (gen.deferredError) {
            playToastSound('error');
            toast.error(gen.deferredError);
            if (!gen.knownFailed) updated.delete(key);
            stateChanged = true;
            needsCreditRefresh = true;
            needsContentFetch = true;
          } else {
            updated.set(key, {
              ...gen,
              progress: 100,
              isPausing: true,
              pauseStartTime: now,
              pollingStartTime: gen.pollingStartTime ?? (gen.isPolling ? Date.now() : undefined),
            });
            stateChanged = true;
          }
        } else {
          // Normal countdown progress.
          const newGen = { ...gen };
          let genChanged = false;

          // Enable DB polling at 40% of duration (or min 60s)
          const pollingDelay = Math.min(60000, totalDuration * 0.4);
          if (!gen.isPolling && effectiveElapsed >= pollingDelay) {
            newGen.isPolling = true;
            newGen.pollingStartTime = gen.pollingStartTime ?? Date.now();
            genChanged = true;
          }

          // Always update progress
          const p = Math.min((effectiveElapsed / totalDuration) * 100, 100);
          if (p !== gen.progress) {
            newGen.progress = p;
            genChanged = true;
          }

          if (genChanged) {
            updated.set(key, newGen);
            stateChanged = true;
          }
        }
      }

      if (stateChanged) {
        setActiveGenerations(updated);
        if (updated.size === 0) {
          setTimeout(() => setIsGenerating(false), 0);
        }
      }

      if (needsCreditRefresh) {
        onCreditUpdate();
      }

      if (needsContentFetch) {
        const contentResult = await getUserContentAction();
        if (contentResult.success && contentResult.data) {
          const sortedData = [...contentResult.data].sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA;
          });
          setUserContent(sortedData);
        }
      }
    }, 30);

    return () => clearInterval(interval);
  }, [activeGenerations, onCreditUpdate]);

  // Trigger polling effect when ANY generation has isPolling=true
  // (this covers both early-polling during simulation AND post-simulation polling)
  const hasPollingItems = Array.from(activeGenerations.values()).some((g) => g.isPolling);

  useEffect(() => {
    if (!hasPollingItems) return;

    const interval = setInterval(async () => {
      // shouldSync=true allows the backend to poll external APIs earlier
      const contentResult = await getUserContentAction(undefined, true);
      if (contentResult.success && contentResult.data) {
        const sortedData = [...contentResult.data].sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });
        setUserContent(sortedData);

        // SYNC ACTIVE GENERATIONS! If polling catches a failure/completion that Pusher missed,
        // we MUST remove it from activeGenerations so it doesn't stay a ghost forever.
        setActiveGenerations((prev) => {
          const updated = new Map(prev);
          let changed = false;

          for (const [key, gen] of updated) {
            const content = contentResult.data.find(
              (c: any) => c.id === gen.taskId || c.taskId === gen.taskId
            );

            if (content && (content.status === 'completed' || content.status === 'failed')) {
              if (content.status === 'failed' && gen.knownFailed) continue;
              updated.delete(key);
              changed = true;
            }
          }

          if (changed && updated.size === 0) {
            setTimeout(() => setIsGenerating(false), 0);
          }
          return changed ? updated : prev;
        });
      }
    }, 15000); // Poll every 15 seconds as a fallback to Webhooks

    return () => clearInterval(interval);
  }, [hasPollingItems]);

  // Robustness: Handle timeouts even without polling interval
  // If an item stays in 'polling' phase for more than 5 minutes, mark as failed
  useEffect(() => {
    const pollingItems = Array.from(activeGenerations.values()).filter(
      (g) => g.phase === 'polling'
    );
    if (pollingItems.length === 0) return;

    const TIMEOUT_MS = 5 * 60 * 1000;
    const checkTimeouts = () => {
      const now = Date.now();
      let changed = false;
      const updated = new Map(activeGenerations);

      for (const [key, gen] of activeGenerations) {
        if (gen.phase === 'polling' && gen.pollingStartTime) {
          if (now - gen.pollingStartTime > TIMEOUT_MS) {
            if (!gen.knownFailed) updated.delete(key);
            changed = true;
            playToastSound('error');
            toast.error('Content generation timed out.');
          }
        }
      }

      if (changed) {
        setActiveGenerations(updated);
        if (updated.size === 0) setIsGenerating(false);
      }
    };

    const interval = setInterval(checkTimeouts, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [activeGenerations]);

  return {
    userContent,
    setUserContent,
    isGenerating,
    setIsGenerating,
    activeGenerations,
    setActiveGenerations,
    loadingTextIndex,
    setLoadingTextIndex,
    showEncouragement,
    galleryState,
    loadingTextAutoplay,
    setLoadingTextAutoplay,
    isInitialLoading,
  };
}
